import { DNVReportStatus } from '../../enums/dnvReportStatusEnum';
import { DNVReportType } from '../../enums/dnvReportTypeEnum';
import { RoleTypeEnum } from '../../enums/roleTypeEnum';
import { SortColumnEnum } from '../../enums/sortVerificationColumnEnum';
import { Role } from '../../types/ViewModel/Role';
import { SelectOption } from '../../types/ViewModel/SelectOption';
import { DnvDataItem } from '../../types/ViewModel/Verification/DnvDataItem';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import { isDateAfter, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';

export const stringifyOptionsArray = (selectOptions: SelectOption[]): string => {
    let stringifiedUrl = '';

    selectOptions.forEach((o, i) => {
        stringifiedUrl += o.value;
        if (i < selectOptions.length - 1) {
            stringifiedUrl += ',';
        }
    });

    return stringifiedUrl;
};

export const disableSimpleSendButton = (reportMetadata: ReportMetadata) => {
    return !canReportBeSentToDnv(reportMetadata);
};

export const isReportDeprecated = (reportMetadata: ReportMetadata) => reportMetadata.isDeprecated === true;

export const getActiveReports = (reportMetadataList: ReportMetadata[] | null | undefined): ReportMetadata[] =>
    reportMetadataList?.filter((reportMetadata) => !isReportDeprecated(reportMetadata)) ?? [];

export const getReportsEligibleForDnvSend = (reportMetadataList: ReportMetadata[] | null | undefined): ReportMetadata[] =>
    getActiveReports(reportMetadataList).filter((reportMetadata) => canReportBeSentToDnv(reportMetadata));

export const showAllSendButton = (isReportMetadataChecked, isAllSelected) => {
    if (isReportMetadataChecked || isAllSelected) {
        return true;
    } else {
        return false;
    }
};

export const showMultiSendCheckbox = (reportMetadata: ReportMetadata) => {
    if (!canReportBeSentToDnv(reportMetadata)) {
        return false;
    } else {
        return true;
    }
};

export const getReportTypeString = (type: DNVReportType) => {
    if (type === DNVReportType.OVDLA) {
        return 'Log abstract';
    } else if (type === DNVReportType.OVDBR) {
        return 'Bunker';
    } else if (type === DNVReportType.OVDBRBdnBased) {
        return 'Bunker (BDN based)';
    } else if (type === DNVReportType.OVDBRDebunkering) {
        return 'Debunkering';
    } else {
        return 'Unknown type';
    }
};

export const getProcessStatusString = (status: DNVReportStatus): string => {
    switch (status) {
        case DNVReportStatus.VoyageOngoing:
            return 'Ongoing voyage';
        case DNVReportStatus.InternalControlIssues:
            return 'Internal issues';
        case DNVReportStatus.ReadyForVerification:
            return 'Ready for verification';
        case DNVReportStatus.SentToDNV:
            return 'Sent to DNV';
        case DNVReportStatus.VerifiedByDNV:
            return 'Verified by DNV';
        case DNVReportStatus.DNVIssues:
            return 'Verification issues';
        case DNVReportStatus.NewVersionAvailable:
            return 'New version available';
    }
};

export const compareDateBefore = (a: Date | string, b: Date | string): number => {
    if (!a || isDateBefore(a, b)) {
        return -1;
    } else if (!b || isDateAfter(a, b)) {
        return 1;
    }
};

export const compareDateAfter = (a: Date | string, b: Date | string): number => {
    if (!a || isDateBefore(a, b)) {
        return 1;
    } else if (!b || isDateAfter(a, b)) {
        return -1;
    }
};

export const compareIssuesAndWarningsAsc = (
    status1: DNVReportStatus,
    status2: DNVReportStatus,
    numInternalIssues1: number,
    numInternalIssues2: number,
    numIssues1: number,
    numIssues2: number,
    numWarnings1: number,
    numWarnings2: number
): number => {
    if (status1 === DNVReportStatus.InternalControlIssues && status2 !== DNVReportStatus.InternalControlIssues) {
        return 1;
    } else if (status2 === DNVReportStatus.InternalControlIssues && status1 !== DNVReportStatus.InternalControlIssues) {
        return -1;
    } else if (
        (status1 === DNVReportStatus.VoyageOngoing || status1 === DNVReportStatus.ReadyForVerification) &&
        status2 !== DNVReportStatus.VoyageOngoing &&
        status2 !== DNVReportStatus.ReadyForVerification
    ) {
        return -1;
    } else if (
        (status2 === DNVReportStatus.VoyageOngoing || status2 === DNVReportStatus.ReadyForVerification) &&
        status1 !== DNVReportStatus.VoyageOngoing &&
        status1 !== DNVReportStatus.ReadyForVerification
    ) {
        return 1;
    } else {
        return numInternalIssues1 === numInternalIssues2
            ? numIssues1 === numIssues2
                ? numWarnings1 > numWarnings2
                    ? 1
                    : -1
                : numIssues1 > numIssues2
                ? 1
                : -1
            : numInternalIssues1 > numInternalIssues2
            ? 1
            : -1;
    }
};

export const compareIssuesAndWarningsDesc = (
    status1: DNVReportStatus,
    status2: DNVReportStatus,
    numInternalIssues1: number,
    numInternalIssues2: number,
    numIssues1: number,
    numIssues2: number,
    numWarnings1: number,
    numWarnings2: number
): number => {
    if (status1 === DNVReportStatus.InternalControlIssues && status2 !== DNVReportStatus.InternalControlIssues) {
        return -1;
    } else if (status2 === DNVReportStatus.InternalControlIssues && status1 !== DNVReportStatus.InternalControlIssues) {
        return 1;
    } else if (
        (status1 === DNVReportStatus.VoyageOngoing || status1 === DNVReportStatus.ReadyForVerification) &&
        status2 !== DNVReportStatus.VoyageOngoing &&
        status2 !== DNVReportStatus.ReadyForVerification
    ) {
        return 1;
    } else if (
        (status2 === DNVReportStatus.VoyageOngoing || status2 === DNVReportStatus.ReadyForVerification) &&
        status1 !== DNVReportStatus.VoyageOngoing &&
        status1 !== DNVReportStatus.ReadyForVerification
    ) {
        return -1;
    } else {
        return numInternalIssues1 === numInternalIssues2
            ? numIssues1 === numIssues2
                ? numWarnings1 > numWarnings2
                    ? -1
                    : 1
                : numIssues1 > numIssues2
                ? -1
                : 1
            : numInternalIssues1 > numInternalIssues2
            ? -1
            : 1;
    }
};

export const compareVesselNameDesc = (a: string, b: string): number => {
    if (a && !b) {
        return 1;
    } else if (!a && b) {
        return 1;
    } else if (a && b) {
        return a > b ? -1 : 1;
    }
};

export const compareVesselNameAsc = (a: string, b: string): number => {
    if (a && !b) {
        return 1;
    } else if (!a && b) {
        return 1;
    } else if (a && b) {
        return a > b ? 1 : -1;
    }
};

export const findMaxdate = (verificationList: DnvDataItem[]) => {
    let maxDate = verificationList[0].updatedDateTime;

    verificationList.forEach((v) => {
        if (v.updatedDateTime > maxDate) {
            maxDate = v.updatedDateTime;
        }
    });
    return maxDate;
};

export const sortList = (filteredReportMetadataList: ReportMetadata[], sortCol: SortColumnEnum, sortDesc: boolean) => {
    const _isSortDesc = sortDesc;
    let _filteredReportMetadataList = filteredReportMetadataList;

    if (sortCol === SortColumnEnum.VesselName && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareVesselNameDesc(a.vesselName, b.vesselName));
    } else if (sortCol === SortColumnEnum.VesselName && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareVesselNameAsc(a.vesselName, b.vesselName));
    } else if (sortCol === SortColumnEnum.ReportType && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => (Number(a.reportType) > Number(b.reportType) ? 1 : -1));
    } else if (sortCol === SortColumnEnum.ReportType && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => (Number(a.reportType) < Number(b.reportType) ? 1 : -1));
    } else if (sortCol === SortColumnEnum.VerificationDate && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateBefore(a.maxVerificationDate, b.maxVerificationDate));
    } else if (sortCol === SortColumnEnum.VerificationDate && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateAfter(a.maxVerificationDate, b.maxVerificationDate));
    } else if (sortCol === SortColumnEnum.SentToDNV && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateBefore(a.sentToDNVAtTimestamp, b.sentToDNVAtTimestamp));
    } else if (sortCol === SortColumnEnum.SentToDNV && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateAfter(a.sentToDNVAtTimestamp, b.sentToDNVAtTimestamp));
    } else if (sortCol === SortColumnEnum.PeriodStart && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateBefore(a.voyageDateTimeUtcFrom, b.voyageDateTimeUtcFrom));
    } else if (sortCol === SortColumnEnum.PeriodStart && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateAfter(a.voyageDateTimeUtcFrom, b.voyageDateTimeUtcFrom));
    } else if (sortCol === SortColumnEnum.PeriodEnd && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateBefore(a.voyageDateTimeUtcTo, b.voyageDateTimeUtcTo));
    } else if (sortCol === SortColumnEnum.PeriodEnd && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => compareDateAfter(a.voyageDateTimeUtcTo, b.voyageDateTimeUtcTo));
    } else if (sortCol === SortColumnEnum.NumberOfIssues && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) =>
            compareIssuesAndWarningsAsc(
                a.status,
                b.status,
                numInternalIssues(a),
                numInternalIssues(b),
                a.numIssues,
                b.numIssues,
                a.numWarnings,
                b.numWarnings
            )
        );
    } else if (sortCol === SortColumnEnum.NumberOfIssues && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) =>
            compareIssuesAndWarningsDesc(
                a.status,
                b.status,
                numInternalIssues(a),
                numInternalIssues(b),
                a.numIssues,
                b.numIssues,
                a.numWarnings,
                b.numWarnings
            )
        );
    } else if (sortCol === SortColumnEnum.Status && _isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => (Number(a.status) > Number(b.status) ? 1 : -1));
    } else if (sortCol === SortColumnEnum.Status && !_isSortDesc) {
        _filteredReportMetadataList = _filteredReportMetadataList.sort((a, b) => (Number(a.status) > Number(b.status) ? -1 : 1));
    }

    return _filteredReportMetadataList;
};

export const shouldIssuesBeHidden = (reportMetadata: ReportMetadata) => {
    if (
        reportMetadata.status === DNVReportStatus.VoyageOngoing ||
        reportMetadata.status === DNVReportStatus.ReadyForVerification ||
        (reportMetadata.numIssues === 0 && reportMetadata.numWarnings === 0 && numInternalIssues(reportMetadata) === 0)
    ) {
        return true;
    } else {
        return false;
    }
};

export const canReportBeSentToDnv = (reportMetadata: ReportMetadata) => {
    if (
        isReportDeprecated(reportMetadata) ||
        !reportMetadata.latestReportFileName ||
        (reportMetadata.reportVersionSentToDNV && reportMetadata.latestReportVersion == reportMetadata.reportVersionSentToDNV)
    ) {
        return false;
    } else {
        return true;
    }
};

export const isReportMetadataChecked = (selectedReportMetadataList: ReportMetadata[], reportMetadata: ReportMetadata) => {
    if (
        selectedReportMetadataList &&
        selectedReportMetadataList.length > 0 &&
        selectedReportMetadataList?.some((r) => r.reportId === reportMetadata.reportId)
    ) {
        return true;
    } else {
        return false;
    }
};

export const getVerificationTooltip = (v: ReportMetadata) => {
    if (v.status === DNVReportStatus.VoyageOngoing || v.status === DNVReportStatus.ReadyForVerification) {
        return '';
    } else {
        return (
            numInternalIssues(v) +
            ' Internal issues ' +
            (v.verificationList?.filter((v) => v.severity === 'Issue')?.length ?? 0) +
            ' Issues ' +
            (v.verificationList?.filter((v) => v.severity === 'Warning')?.length ?? 0) +
            ' Warnings'
        );
    }
};

export const numInternalIssues = (reportMetadata: ReportMetadata) => {
    return (
        (reportMetadata.missingAolIssues?.length ?? 0) +
        (reportMetadata.missingPositionsIssues?.length ?? 0) +
        (reportMetadata.timeInconsistencyIssues?.length ?? 0) +
        (reportMetadata.reportFormatIssues?.length ?? 0)
    );
};

export const compareVerificationIssues = (a: DnvDataItem, b: DnvDataItem, sortDesc) => {
    if (a.severity > b.severity) {
        return 1;
    }
    if (a.severity < b.severity) {
        return -1;
    }
    if (a?.eventDateTime && b?.eventDateTime && !sortDesc) {
        return compareDateAfter(a.eventDateTime, b.eventDateTime);
    }
    if (a?.eventDateTime && b?.eventDateTime && sortDesc) {
        return compareDateBefore(a.eventDateTime, b.eventDateTime);
    }
    if (a?.eventDateTime && b?.eventDateTime && !sortDesc) {
        return compareDateAfter(a.eventDateTime, b.eventDateTime);
    }
    if (a?.legStart && b?.legStart && sortDesc) {
        return compareDateBefore(a.legStart, b.legStart);
    }
    if (a?.legStart && b?.legStart && !sortDesc) {
        return compareDateAfter(a.legStart, b.legStart);
    }
};

export const canShowComponentForRole = (role: Role) => {
    if (role?.roleType === RoleTypeEnum.Admin || role?.roleType === RoleTypeEnum.FleetPerformanceAnalyst) {
        return true;
    } else {
        return false;
    }
};

/** Returns true for roles that can send reports to DNV and accept issues (Admin, FleetPerformanceAnalyst). */
export const canSendOrAcceptForRole = (role: Role) => {
    return role?.roleType === RoleTypeEnum.Admin || role?.roleType === RoleTypeEnum.FleetPerformanceAnalyst;
};

/** Returns true for roles that can add/edit comments (Admin, FleetPerformanceAnalyst, ShipUser). */
export const canCommentForRole = (role: Role) => {
    return (
        role?.roleType === RoleTypeEnum.Admin ||
        role?.roleType === RoleTypeEnum.FleetPerformanceAnalyst ||
        role?.roleType === RoleTypeEnum.ShipUser
    );
};

/** Returns true if the role has any access to the verification module (Admin, FleetPerformanceAnalyst, ShipUser). */
export const canAccessVerificationForRole = (role: Role) => {
    return (
        role?.roleType === RoleTypeEnum.Admin ||
        role?.roleType === RoleTypeEnum.FleetPerformanceAnalyst ||
        role?.roleType === RoleTypeEnum.ShipUser
    );
};

/** Returns true if the role is a ship user with limited verification access. */
export const isShipUserRole = (role: Role) => {
    return role?.roleType === RoleTypeEnum.ShipUser;
};
